/* global React, window, Card, Button, EmptyState, t, toast */
// ============================================================
// PLUMME 3 — Ansattportal-port for brukere uten eget foretak
//
// En ansatt er IKKE medlem av foretaket. Tilgangen ligger i
// `employeePortalAccess` og leses av `getEmployeePortalHttp`, ikke av
// medlemskapene. Uten denne porten stoppet app.jsx enhver innlogget bruker
// uten `activeBusinessId` pa «fant ikke foretak» — altsa nettopp den som ble
// invitert som ansatt og ikke har noe foretak selv.
//
// Porten er bevisst lagt utenfor <Shell>: ansattportalen tegner sin egen
// fullflate med egen sidebar, og Shell-navigasjonen peker pa ruter en ansatt
// verken har tilgang til eller skal se.
// ============================================================
(function (global) {
  'use strict';

  const { useState, useEffect, useRef } = global.React;

  const signOutNow = async () => {
    try { await global.PlummeBackend?.signOut?.(); }
    catch (error) { global.toast?.(error?.message || t('shell.client.signOutFailed'), 'error'); }
  };

  // `active` skrus pa mens oppslaget pagar ogsa. Vi vet enna ikke om brukeren er
  // ansatt et sted, og et blink av «fant ikke foretak» for portalen dukker opp er
  // verre enn et halvsekund ekstra lastetekst.
  function useEmployeeOnlyPortal(backend, route, setRoute) {
    // `loading` staar paa fra `onAuthStateChanged` til foretakstilgangen er lest, og
    // i det vinduet er `activeBusinessId` tom OGSA for en vanlig bruker med eget
    // foretak. Leser vi den tomheten som «ansatt uten foretak», kaprer porten ruten
    // til ansattportal for foretaket rekker a lande — og brukeren blir staaende der
    // med «Ingen arbeidsgivere» inne i sitt eget skall. Vent til oppstarten er ferdig.
    const enabled = Boolean(backend?.configured && backend?.user && !backend?.loading && !backend?.activeBusinessId);
    const uid = backend?.user?.uid || '';
    const [portals, setPortals] = useState({ userId: enabled ? uid : '', loading: enabled, has: false, unverified: false });
    const emailVerified = backend?.user?.emailVerified === true;
    const onboardingPending = Boolean(global.localStorage?.getItem('plumme-open-onboarding-after-signup'));
    useEffect(() => {
      let cancelled = false;
      if (!enabled || !global.PlummeBackend?.getEmployeePortals) {
        setPortals({ userId: '', loading: false, has: false, unverified: false });
        return () => { cancelled = true; };
      }
      setPortals({ userId: uid, loading: true, has: false, unverified: false });
      global.PlummeBackend.getEmployeePortals()
        .then((rows) => { if (!cancelled) setPortals({ userId: uid, loading: false, has: Array.isArray(rows) && rows.length > 0, unverified: false }); })
        // En invitert ansatt som ikke har bekreftet e-posten sin far 412 her. Hen
        // har en reell tilgang og skal se hva som mangler, ikke «fant ikke foretak».
        .catch((error) => { if (!cancelled) setPortals({ userId: uid, loading: false, has: false, unverified: error?.domainCode === 'auth.emailNotVerified' }); });
      return () => { cancelled = true; };
    }, [enabled, uid, emailVerified]);
    const active = enabled && (portals.userId !== uid || portals.loading || portals.has || portals.unverified);
    useEffect(() => {
      if (portals.has && onboardingPending) {
        global.localStorage?.removeItem('plumme-open-onboarding-after-signup');
      }
    }, [portals.has, onboardingPending]);
    // Ruten ma settes for at sidelasteren skal hente ansatt.jsx og sprakfilene
    // den hoerer sammen med — de er merket `data-routes="ansattportal"`.
    //
    // Ruten er delt med resten av appen og lagres i `plumme-route` og i adressen.
    // Slipper porten taket igjen — foretaket kom likevel, eller tilgangen falt bort —
    // ma vi levere ruten tilbake der vi hentet den. Ellers arver Shell en
    // ansattportal-rute som ikke er brukerens eget valg.
    const hijackedFrom = useRef(null);
    const employeeRouteActive = enabled && portals.has;
    useEffect(() => {
      if (typeof setRoute !== 'function') return;
      // A self-registering account must stay in the ordinary account flow. Only a
      // confirmed employee access result is allowed to claim /ansattportal.
      if (onboardingPending && portals.unverified && route === 'ansattportal') {
        setRoute('hjem');
        return;
      }
      if (employeeRouteActive) {
        if (route !== 'ansattportal') { hijackedFrom.current = route; setRoute('ansattportal'); }
        return;
      }
      if (hijackedFrom.current && route === 'ansattportal') setRoute(hijackedFrom.current);
      hijackedFrom.current = null;
    }, [employeeRouteActive, onboardingPending, portals.unverified, route]);
    return {
      active,
      ready: Boolean(portals.has && global.AnsattPortalPage),
      unverified: portals.unverified,
      delivery: backend?.emailVerificationDelivery || null,
    };
  }

  function AccountEmailVerifyNotice({ delivery }) {
    const [busy, setBusy] = useState('');
    const resend = async () => {
      setBusy('resend');
      try {
        const sent = await global.PlummeBackend?.resendEmailVerification?.();
        toast(sent === false
          ? t('app.employeePortalVerifyAlready', 'E-postadressen er allerede bekreftet. Last siden pa nytt.')
          : t('app.employeePortalVerifySent', 'Bekreftelseslenke er sendt pa nytt.'), 'success');
      } catch (error) {
        toast(error?.message || t('app.employeePortalVerifyFailed', 'Kunne ikke sende bekreftelseslenke.'), 'error');
      } finally { setBusy(''); }
    };
    const check = async () => {
      setBusy('check');
      try {
        const verified = await global.PlummeBackend?.reloadEmailVerification?.();
        toast(verified
          ? t('app.emailVerifyConfirmed', 'E-postadressen er bekreftet.')
          : t('app.emailVerifyNotConfirmed', 'E-postadressen er ikke bekreftet ennå. Åpne lenken i e-posten og prøv igjen.'), verified ? 'success' : 'info');
      } catch (error) {
        toast(error?.message || t('app.emailVerifyCheckFailed', 'Kunne ikke kontrollere e-postadressen.'), 'error');
      } finally { setBusy(''); }
    };
    const email = global.PlummeBackend?.user?.email || '';
    return <div style={{ maxWidth: 560, margin: '48px auto', padding: '0 16px' }}><Card>
      {delivery === 'failed' ? <div className="alert alert-warning" style={{ marginBottom: 16 }}>
        <span>{t('app.emailVerifyInitialFailed', 'Kontoen ble opprettet, men bekreftelseslenken kunne ikke sendes. Prøv å sende den på nytt.')}</span>
      </div> : null}
      <EmptyState noVisual
        title={t('app.employeePortalVerifyTitle', 'Bekreft e-postadressen din')}
        text={t('app.employeePortalVerifyText', 'Vi har sendt en bekreftelseslenke til {{email}}. Det kan ta noen minutter før den kommer. Sjekk også søppelpost. Åpne lenken for å fortsette.', { email: email || t('auth.email') })}
        action={<div className="row gap-10 wrap">
          <Button disabled={Boolean(busy)} onClick={resend}>{busy === 'resend' ? t('common.working') : t('app.employeePortalVerifyResend', 'Send lenken på nytt')}</Button>
          <Button variant="secondary" disabled={Boolean(busy)} onClick={check}>{busy === 'check' ? t('common.working') : t('app.emailVerifyCheck', 'Jeg har bekreftet')}</Button>
          <Button variant="secondary" onClick={signOutNow}>{t('common.signOut')}</Button>
        </div>} />
    </Card></div>;
  }

  // `Loading` kommer fra app.jsx — BackendLoading er lokal der og ligger ikke pa window.
  function EmployeePortalSurface({ gate, setRoute, client, setClient, Loading }) {
    if (gate.unverified) return <AccountEmailVerifyNotice delivery={gate.delivery} />;
    if (!gate.ready) return Loading ? <Loading translationKey="app.loadingEmployeePortal" /> : null;
    return <global.AnsattPortalPage setRoute={setRoute} onBack={signOutNow} client={client} setClient={setClient} />;
  }

  global.PlummeEmployeePortalGate = { useEmployeeOnlyPortal, EmployeePortalSurface };
}(window));
